import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';

interface Banner {
  id: number;
  image_url: string;
  mobile_image_url?: string;
  title?: string;
  subtitle?: string;
  discount_text?: string;
  button_text?: string;
  button_link?: string;
  bg_color: string;
  text_color: string;
  badge_color: string;
  is_active: boolean;
  sort_order: number;
}

const AdminBanners: React.FC = () => {
  const [banners, setBanners] = useState<Banner[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState<Banner | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    discount_text: '',
    button_text: 'Shop Now',
    button_link: '/product-signature',
    bg_color: 'from-blue-500 to-purple-500',
    text_color: 'text-white',
    badge_color: 'bg-white text-blue-500',
    is_active: true,
    sort_order: 0,
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [mobileImageFile, setMobileImageFile] = useState<File | null>(null);
  const [mobileImagePreview, setMobileImagePreview] = useState<string | null>(null);

  useEffect(() => {
    loadBanners();
  }, []);

  const loadBanners = async () => {
    try {
      const response = await apiService.get('/admin/banners');
      setBanners(response.data || response);
    } catch (error) {
      // Error loading banners
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleMobileImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setMobileImageFile(file);
      setMobileImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const data = new FormData();
    if (imageFile) {
      data.append('image', imageFile);
    }
    if (mobileImageFile) {
      data.append('mobile_image', mobileImageFile);
    }
    
    // Only append non-empty values
    if (formData.title) data.append('title', formData.title);
    if (formData.subtitle) data.append('subtitle', formData.subtitle);
    if (formData.discount_text) data.append('discount_text', formData.discount_text);
    if (formData.button_text) data.append('button_text', formData.button_text);
    if (formData.button_link) data.append('button_link', formData.button_link);
    
    data.append('bg_color', formData.bg_color);
    data.append('text_color', formData.text_color);
    data.append('badge_color', formData.badge_color);
    data.append('is_active', formData.is_active ? '1' : '0');
    data.append('sort_order', formData.sort_order.toString());

    try {
      if (editingBanner) {
        await apiService.post(`/admin/banners/${editingBanner.id}?_method=PUT`, data);
      } else {
        await apiService.post('/admin/banners', data);
      }
      
      loadBanners();
      handleCloseModal();
    } catch (error) {
      alert('Failed to save banner');
    }
  };

  const handleEdit = (banner: Banner) => {
    setEditingBanner(banner);
    setFormData({
      title: banner.title || '',
      subtitle: banner.subtitle || '',
      discount_text: banner.discount_text || '',
      button_text: banner.button_text || 'Shop Now',
      button_link: banner.button_link || '/product-signature',
      bg_color: banner.bg_color,
      text_color: banner.text_color,
      badge_color: banner.badge_color,
      is_active: banner.is_active,
      sort_order: banner.sort_order,
    });
    setImagePreview(banner.image_url);
    setMobileImageFile(null);
    setMobileImagePreview(banner.mobile_image_url || null);
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this banner?')) return;
    
    try {
      await apiService.delete(`/admin/banners/${id}`);
      loadBanners();
    } catch (error) {
      alert('Failed to delete banner');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingBanner(null);
    setFormData({
      title: '',
      subtitle: '',
      discount_text: '',
      button_text: 'Shop Now',
      button_link: '/product-signature',
      bg_color: 'from-blue-500 to-purple-500',
      text_color: 'text-white',
      badge_color: 'bg-white text-blue-500',
      is_active: true,
      sort_order: 0,
    });
    setImageFile(null);
    setImagePreview(null);
    setMobileImageFile(null);
    setMobileImagePreview(null);
  };

  if (loading) return <div className="p-6">Loading...</div>;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Banner Management</h1>
        <button
          onClick={() => setShowModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Add Banner
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {banners.map((banner) => (
          <div key={banner.id} className="bg-white rounded-lg shadow p-4">
            <div className="relative mb-4">
              <img
                src={banner.image_url}
                alt={banner.title || 'Banner'}
                className="w-full h-48 object-cover rounded"
              />
              <span
                className={`absolute top-2 right-2 px-2 py-1 rounded text-xs font-medium ${
                  banner.mobile_image_url
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-200 text-gray-600'
                }`}
                title={banner.mobile_image_url ? 'Has a separate mobile image' : 'Uses desktop image on mobile'}
              >
                {banner.mobile_image_url ? 'Mobile ✓' : 'No mobile'}
              </span>
            </div>
            <h3 className="font-bold mb-2">{banner.title || 'No Title'}</h3>
            <p className="text-sm text-gray-600 mb-2">{banner.subtitle || 'No Subtitle'}</p>
            <p className="text-sm text-gray-600 mb-4">{banner.discount_text || 'No Discount Text'}</p>
            <div className="flex items-center justify-between mb-4">
              <span className={`px-2 py-1 rounded text-xs ${banner.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                {banner.is_active ? 'Active' : 'Inactive'}
              </span>
              <span className="text-xs text-gray-500">Order: {banner.sort_order}</span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handleEdit(banner)}
                className="flex-1 bg-blue-600 text-white px-3 py-2 rounded text-sm hover:bg-blue-700"
              >
                Edit
              </button>
              <button
                onClick={() => handleDelete(banner.id)}
                className="flex-1 bg-red-600 text-white px-3 py-2 rounded text-sm hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-xl font-bold mb-4">{editingBanner ? 'Edit Banner' : 'Add Banner'}</h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Desktop / Large Screen Image *</label>
                <div className="mb-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800">
                  <p className="font-semibold">Recommended for desktop / large screens</p>
                  <ul className="mt-1 list-disc list-inside space-y-0.5">
                    <li>Size: <strong>1920 × 800 px</strong> (wide / landscape)</li>
                    <li>Aspect ratio: <strong>16:9</strong> or wider</li>
                    <li>Format: JPG, PNG or WEBP &middot; Max 5 MB</li>
                    <li>Keep important text/subject in the center</li>
                  </ul>
                </div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  required={!editingBanner}
                  className="w-full border rounded px-3 py-2"
                />
                {imagePreview && (
                  <img src={imagePreview} alt="Desktop preview" className="mt-2 w-full h-48 object-cover rounded" />
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Mobile Image (Optional)</label>
                <div className="mb-2 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-800">
                  <p className="font-semibold">Recommended for mobile / phones</p>
                  <ul className="mt-1 list-disc list-inside space-y-0.5">
                    <li>Size: <strong>1080 × 1350 px</strong> (portrait / tall)</li>
                    <li>Aspect ratio: <strong>4:5</strong> or <strong>1:1</strong> (square)</li>
                    <li>Format: JPG, PNG or WEBP &middot; Max 5 MB</li>
                    <li>If left empty, the desktop image is used on mobile</li>
                  </ul>
                </div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleMobileImageChange}
                  className="w-full border rounded px-3 py-2"
                />
                {mobileImagePreview && (
                  <img src={mobileImagePreview} alt="Mobile preview" className="mt-2 w-48 h-64 object-cover rounded mx-auto" />
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Title (Optional)</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="w-full border rounded px-3 py-2"
                  placeholder="e.g., SPECIAL PROMOTION"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Subtitle (Optional)</label>
                <input
                  type="text"
                  value={formData.subtitle}
                  onChange={(e) => setFormData({...formData, subtitle: e.target.value})}
                  className="w-full border rounded px-3 py-2"
                  placeholder="e.g., Fresh Collection 2025"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Discount Text (Optional)</label>
                <input
                  type="text"
                  value={formData.discount_text}
                  onChange={(e) => setFormData({...formData, discount_text: e.target.value})}
                  className="w-full border rounded px-3 py-2"
                  placeholder="e.g., 10% OFF"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Button Text (Optional)</label>
                <input
                  type="text"
                  value={formData.button_text}
                  onChange={(e) => setFormData({...formData, button_text: e.target.value})}
                  className="w-full border rounded px-3 py-2"
                  placeholder="e.g., Shop Now"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Button Link (Optional)</label>
                <input
                  type="text"
                  value={formData.button_link}
                  onChange={(e) => setFormData({...formData, button_link: e.target.value})}
                  className="w-full border rounded px-3 py-2"
                  placeholder="e.g., /product-signature"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Sort Order</label>
                  <input
                    type="number"
                    value={formData.sort_order}
                    onChange={(e) => setFormData({...formData, sort_order: parseInt(e.target.value)})}
                    className="w-full border rounded px-3 py-2"
                  />
                </div>
                <div>
                  <label className="flex items-center mt-8">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                      className="mr-2"
                    />
                    Active
                  </label>
                </div>
              </div>

              <div className="flex gap-2 pt-4">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                >
                  {editingBanner ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBanners;
